import html_default from "./template.default.html?raw";
const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("default" == app_theme) {
  html = html_default;
}
import { computed, inject, onMounted, onUnmounted, ref, nextTick, type SetupContext } from "vue";
import type { LoginService } from "../../application/services/LoginService.ts";
import type { UserService } from "../../application/services/UserService.ts";

interface Props {
  // Vorbelegung der E-Mail (z. B. aus dem Gast-Formular, wenn die Adresse bekannt ist)
  email?: string;
  // Text ueber dem Formular, z. B. "Ich habe schon einmal gebucht"
  title?: string;
  // Link "Lieber mit Passwort anmelden" anzeigen
  showPasswordLink?: boolean;
  // Knopf "Zurueck" anzeigen (nur sinnvoll, wenn der Elternteil onCancel behandelt)
  showCancel?: boolean;
}

// Login mit Code per E-Mail: E-Mail eingeben -> Code aus der Mail eintippen -> eingeloggt.
// Unbekannte E-Mail: Hinweis mit Knopf "Als neue Person weiter" (Ereignis onUnknownEmail).
export default {
  template: html,
  emits: {
    onLoggedIn: () => true,
    onUnknownEmail: (email: string) => typeof email === "string",
    onCancel: () => true,
  },
  props: {
    email: { type: String, default: "" },
    title: { type: String, default: "Ich habe schon einmal gebucht" },
    showPasswordLink: { type: Boolean, default: true },
    showCancel: { type: Boolean, default: true },
  },
  setup(props: Props, { emit }: SetupContext) {
    const loginService: LoginService | undefined = inject("loginService");
    const userService: UserService | undefined = inject("userService");

    const phase = ref<"email" | "code" | "unknown">("email");
    const email = ref(props.email ?? "");
    const code = ref("");
    const session = ref("");
    const busy = ref(false);
    const error = ref("");
    const info = ref("");
    const resendSeconds = ref(0);
    const codeInput = ref<HTMLInputElement | null>(null);
    let resendTimer: number | undefined;

    const canResend = computed(() => resendSeconds.value <= 0 && !busy.value);
    const codeDigits = computed(() => code.value.replace(/\D/g, ""));
    const codeComplete = computed(() => codeDigits.value.length == 6);

    const startResendTimer = function () {
      resendSeconds.value = 30;
      if (resendTimer) {
        window.clearInterval(resendTimer);
      }
      resendTimer = window.setInterval(function () {
        resendSeconds.value -= 1;
        if (resendSeconds.value <= 0 && resendTimer) {
          window.clearInterval(resendTimer);
          resendTimer = undefined;
        }
      }, 1000);
    };

    const requestCode = async function () {
      error.value = "";
      info.value = "";
      if (!loginService) {
        error.value = "Systemfehler: LoginService fehlt";
        return;
      }
      const value = email.value.trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
        error.value = "Bitte eine gültige E-Mail-Adresse eingeben.";
        return;
      }
      busy.value = true;
      try {
        const res = await loginService.start(value);
        if (!res.known) {
          phase.value = "unknown";
          info.value = res.message;
          return;
        }
        email.value = res.email;
        session.value = res.session;
        code.value = "";
        phase.value = "code";
        info.value = res.message;
        startResendTimer();
        await nextTick();
        codeInput.value?.focus();
      } catch (err) {
        error.value = (err as Error).message;
      } finally {
        busy.value = false;
      }
    };

    const verifyCode = async function () {
      error.value = "";
      if (!loginService || !codeComplete.value) {
        return;
      }
      busy.value = true;
      try {
        const res = await loginService.verify(email.value, session.value, codeDigits.value);
        if (res.success) {
          info.value = "";
          emit("onLoggedIn");
          return;
        }
        if (res.canRetry && res.session) {
          session.value = res.session;
          code.value = "";
          error.value = res.message;
          await nextTick();
          codeInput.value?.focus();
        } else {
          // Session abgelaufen oder zu viele Versuche: von vorne
          session.value = "";
          code.value = "";
          phase.value = "email";
          error.value = res.message;
        }
      } catch (err) {
        error.value = (err as Error).message;
      } finally {
        busy.value = false;
      }
    };

    const onCodeInput = function () {
      // Nur Ziffern, hoechstens 6, automatisch pruefen sobald vollstaendig
      const digits = code.value.replace(/\D/g, "").slice(0, 6);
      code.value = digits.length > 3 ? `${digits.slice(0, 3)} ${digits.slice(3)}` : digits;
      if (digits.length == 6) {
        void verifyCode();
      }
    };

    const changeEmail = function () {
      phase.value = "email";
      code.value = "";
      session.value = "";
      error.value = "";
      info.value = "";
    };

    const continueAsNew = function () {
      emit("onUnknownEmail", email.value.trim());
    };

    const loginWithPassword = async function () {
      if (userService) {
        await userService.setAfterLoginUrl(location.href);
        location.href = await userService.getLoginUrl();
      }
    };

    onMounted(function () {
      if (email.value.trim() != "") {
        // E-Mail schon bekannt (aus dem Gast-Formular): direkt Code anfordern
        void requestCode();
      }
    });

    onUnmounted(function () {
      if (resendTimer) {
        window.clearInterval(resendTimer);
      }
    });

    return {
      props,
      phase,
      email,
      code,
      busy,
      error,
      info,
      resendSeconds,
      canResend,
      codeComplete,
      codeInput,
      requestCode,
      verifyCode,
      onCodeInput,
      changeEmail,
      continueAsNew,
      loginWithPassword,
      cancel: () => emit("onCancel"),
    };
  },
};
